import { assign } from '@thoughtspot/string-util';
import DOMPurify from 'dompurify';
import _ from 'lodash';
import { generateFontStyleCSS } from '../formatting-util';
import { ChartType } from '../types/prism';
import { extractCaptionTitleAndLink, getDataLabelFromColumn, getFormattedValueForColumnTooltip, getXAxisColumnLabelAt, } from './formatter-service';
import { TOOLTIP_TEMPLATE } from './highcharts/highchart-formatter-templates';
export var TooltipLabelType;
(function (TooltipLabelType) {
    TooltipLabelType["X_LABEL"] = "X_LABEL";
    TooltipLabelType["Y_LABEL"] = "Y_LABEL";
    TooltipLabelType["SERIES_LABEL"] = "SERIES_LABEL";
    TooltipLabelType["CATEGORY_LABEL"] = "CATEGORY_LABEL";
    TooltipLabelType["RADIAL_LABEL"] = "RADIAL_LABEL";
    TooltipLabelType["TOOLTIP_CONFIG_LABEL"] = "TOOLTIP_CONFIG_LABEL";
})(TooltipLabelType || (TooltipLabelType = {}));
const RADIAL_COLUMN_TITLE_CLASS = 'radialColumnTitle';
const RADIAL_VALUE_CLASS = 'radialValue';
export function getClassByLabelType(labelType, dataObject) {
    switch (labelType) {
        case TooltipLabelType.X_LABEL:
            return {
                labelClass: 'xAxisTitle',
                valueClass: 'pointX',
            };
        case TooltipLabelType.Y_LABEL:
            return {
                labelClass: 'yAxisTitle',
                valueClass: 'pointY',
            };
        case TooltipLabelType.CATEGORY_LABEL:
            return {
                labelClass: 'categoryTitle',
                valueClass: 'categoryName',
            };
        case TooltipLabelType.RADIAL_LABEL:
            return {
                labelClass: RADIAL_COLUMN_TITLE_CLASS,
                valueClass: RADIAL_VALUE_CLASS,
            };
        case TooltipLabelType.SERIES_LABEL:
            return {
                labelClass: 'seriesTitle',
                valueClass: 'seriesValue',
                valueStyle: dataObject.valueColor
                    ? `background-color:${dataObject.valueColor}`
                    : '',
            };
        case TooltipLabelType.TOOLTIP_CONFIG_LABEL:
            return {
                labelClass: 'seriesTitle',
                valueClass: 'seriesValue',
                valueStyle: dataObject.valueColor
                    ? `background-color:${dataObject.valueColor}`
                    : '',
            };
        default:
            return {
                labelClass: '',
                valueClass: '',
            };
    }
}
export function getEffectiveYAxisColumn(seriesInfo, params) {
    const yAxisColumns = params.yAxisColumns.map(colId => params.allColumns[colId]);
    const yAxisColumn = yAxisColumns.find(col => col.id === seriesInfo.valueColumnIdentifier);
    return yAxisColumn;
}
export const chartSpecGetYAxisTooltipData = {
    PARETO(seriesInfo, params) {
        let yAxisColumn = getEffectiveYAxisColumn(seriesInfo, params);
        if (!yAxisColumn) {
            const yAxisColumnID = params.yAxisColumns[0];
            yAxisColumn = params.allColumns[yAxisColumnID];
        }
        if (seriesInfo.tooltipFormatter) {
            const yLabel = seriesInfo.tooltipFormatter(seriesInfo.y);
            return {
                label: yAxisColumn.name,
                value: yLabel,
                labelType: TooltipLabelType.Y_LABEL,
            };
        }
        return {
            label: yAxisColumn.name,
            value: getDataLabelFromColumn(yAxisColumn, seriesInfo.y, params.yValueToRawValues),
            labelType: TooltipLabelType.Y_LABEL,
        };
    },
    CANDLESTICK(seriesInfo, params) {
        const CANDLE_STICK_CHART_DATA_KEYS = ['open', 'high', 'low', 'close'];
        const yAxisTooltipData = [];
        CANDLE_STICK_CHART_DATA_KEYS.forEach((key, idx) => {
            const yAxisColumn = params.allColumns[params.yAxisColumns[idx]];
            yAxisTooltipData.push({
                label: yAxisColumn.name,
                value: getDataLabelFromColumn(yAxisColumn, _.get(seriesInfo, [key]), params.yValueToRawValues),
            });
        });
        return yAxisTooltipData;
    },
};
export const getYAxisTooltipData = (seriesInfo, params, colId) => {
    const chartType = params.chartType;
    if (chartType === ChartType.Pareto || chartType === ChartType.Candlestick) {
        return chartSpecGetYAxisTooltipData[chartType](seriesInfo, params);
    }
    const yAxisColumn = getEffectiveYAxisColumn(seriesInfo, params);
    if (colId && yAxisColumn.id !== colId) {
        return undefined;
    }
    if (!yAxisColumn) {
        let yLabel = '';
        if (!seriesInfo.tooltipFormatter) {
            yLabel = `${seriesInfo.y}`;
        }
        else {
            yLabel = seriesInfo.tooltipFormatter(seriesInfo.y);
        }
        return {
            label: '',
            value: yLabel,
            labelType: TooltipLabelType.Y_LABEL,
        };
    }
    const value = getDataLabelFromColumn(yAxisColumn, seriesInfo.y, params.yValueToRawValues);
    return {
        label: yAxisColumn.name,
        value,
        labelType: TooltipLabelType.Y_LABEL,
    };
};
export const getXAxisTooltipData = (seriesInfo, params, colId) => {
    const columns = colId ? [colId] : params.xAxisColumns;
    const xAxisColumns = colId ? [colId] : params.xAxisColumns;
    if (!columns || !columns.length || !columns[0]) {
        return undefined;
    }
    let xValues = params.xValueToRawValues[seriesInfo.x];
    if (colId && xValues) {
        xValues = xValues.filter((value) => value.columnEffectiveId === colId);
    }
    const value = getXAxisColumnLabelAt(seriesInfo.x, xAxisColumns, params.yAxisColumns, params.chartType, xValues, params.allColumns);
    return {
        label: columns.map(colId => params.allColumns[colId].name).join(', '),
        value,
        labelType: TooltipLabelType.X_LABEL,
    };
};
export const getCategoryTooltipData = (seriesInfo, params) => {
    if (_.isUndefined(seriesInfo.categoryName)) {
        return undefined;
    }
    const column = params.allColumns[params.categoryColumns[0]];
    const value = seriesInfo.categoryName;
    const formattedValue = getFormattedValueForColumnTooltip(column, value);
    return {
        label: column.name,
        value: formattedValue,
        labelType: TooltipLabelType.CATEGORY_LABEL,
    };
};
export const getRadialTooltipData = (seriesInfo, params) => {
    if (_.isUndefined(seriesInfo.z)) {
        return undefined;
    }
    const column = params.allColumns[params.radialColumn];
    const value = seriesInfo.z;
    let formattedValue;
    if (value > column.maxRadialValue || value < column.minRadialValue) {
        formattedValue = Infinity;
    }
    else {
        formattedValue = getFormattedValueForColumnTooltip(column, value);
    }
    return {
        label: column.name,
        value: formattedValue,
        labelType: TooltipLabelType.RADIAL_LABEL,
    };
};
export const getSeriesTooltipData = (seriesInfo, params) => {
    if (!seriesInfo.seriesColor) {
        return undefined;
    }
    const yAxisColumns = params.yAxisColumns.map(colId => params.allColumns[colId]);
    const yAxisColumn = getEffectiveYAxisColumn(seriesInfo, params);
    const yAxisColumnEffectiveId = yAxisColumn && yAxisColumn.id;
    const indexOfYAxisColumn = yAxisColumns.findIndex(column => column.id === yAxisColumnEffectiveId);
    if (indexOfYAxisColumn !== 0) {
        return undefined;
    }
    const columns = params.legendColumns &&
        params.legendColumns.map(colId => params.allColumns[colId]);
    if (!columns || !columns.length || !columns[0]) {
        return undefined;
    }
    return {
        label: columns.map(column => column.name).join(', '),
        value: seriesInfo.seriesName,
        labelType: TooltipLabelType.SERIES_LABEL,
        valueColor: seriesInfo.seriesColor,
    };
};
export const generateTooltipContentByAxisConfig = (seriesInfo, props) => {
    return _.compact([
        ..._.castArray(getYAxisTooltipData(seriesInfo, props)),
        getXAxisTooltipData(seriesInfo, props),
        getCategoryTooltipData(seriesInfo, props),
        getRadialTooltipData(seriesInfo, props),
        getSeriesTooltipData(seriesInfo, props),
    ]);
};
export const getTooltipDataLabelType = (props, colId) => {
    if (props.categoryColumns?.includes(colId)) {
        return TooltipLabelType.CATEGORY_LABEL;
    }
    if (props.legendColumns?.includes(colId)) {
        return TooltipLabelType.SERIES_LABEL;
    }
    return TooltipLabelType.TOOLTIP_CONFIG_LABEL;
};
export const getTooltipConfigData = (seriesInfo, props, colId) => {
    const { tooltipDataContent } = seriesInfo;
    if (!tooltipDataContent[colId]) {
        return undefined;
    }
    const { column, dataValue } = tooltipDataContent[colId];
    const formattedValue = getFormattedValueForColumnTooltip(column, dataValue);
    const data = {
        label: column.name,
        value: formattedValue,
        labelType: getTooltipDataLabelType(props, colId),
    };
    if (props.legendColumns?.includes(colId)) {
        data.valueColor = seriesInfo.seriesColor;
    }
    return data;
};
export const mergeConsecutiveTooltipData = (tooltipData) => {
    return tooltipData.reduce((mergedTooltipData, currTooltipData) => {
        const prevData = mergedTooltipData[mergedTooltipData.length - 1];
        const mergeLabelTypes = [
            TooltipLabelType.X_LABEL,
            TooltipLabelType.SERIES_LABEL,
        ];
        if (prevData?.labelType === currTooltipData.labelType &&
            mergeLabelTypes.includes(prevData.labelType)) {
            prevData.label += `, ${currTooltipData.label}`;
            prevData.value += `, ${currTooltipData.value}`;
        }
        else {
            mergedTooltipData.push(currTooltipData);
        }
        return mergedTooltipData;
    }, []);
};
export const generateTooltipContentByTooltipConfig = (seriesInfo, props) => {
    const { tooltipConfig } = props;
    const tooltipData = tooltipConfig.columnIds.map(colId => {
        if (props.yAxisColumns?.includes(colId)) {
            return getYAxisTooltipData(seriesInfo, props, colId);
        }
        if (props.xAxisColumns?.includes(colId)) {
            return getXAxisTooltipData(seriesInfo, props, colId);
        }
        if (props.radialColumn === colId) {
            return getRadialTooltipData(seriesInfo, props);
        }
        return getTooltipConfigData(seriesInfo, props, colId);
    });
    const mergedTooltipData = mergeConsecutiveTooltipData(_.compact(_.flatten(tooltipData)));
    return mergedTooltipData;
};
export const genericTooltipData = (seriesInfo, props) => {
    const { tooltipConfig } = props;
    if (_.isNil(tooltipConfig?.columnIds)) {
        return generateTooltipContentByAxisConfig(seriesInfo, props);
    }
    return generateTooltipContentByTooltipConfig(seriesInfo, props);
};
export function importDefaultModule(requiredModule) {
    return (requiredModule && requiredModule.default) || requiredModule;
}
export function tooltipComponent(tooltipData) {
    const label = tooltipData.label ? `${tooltipData.label}:` : '';
    const labelClass = tooltipData.labelClass;
    let labelStyle = tooltipData.titleStyle || '';
    const customOverrides = tooltipData.customOverrides || {};
    const { label: labelCustomStyle, value: valueCustomStyle, } = customOverrides;
    if (labelCustomStyle) {
        labelStyle += generateFontStyleCSS(labelCustomStyle);
    }
    const parsedValue = extractCaptionTitleAndLink(tooltipData.value);
    let value = parsedValue
        ? parsedValue.caption
        : tooltipData.value.toString();
    const colorStyle = tooltipData.valueStyle || 'display: none';
    const valueClass = tooltipData.valueClass;
    let valueStyle = '';
    if (valueCustomStyle) {
        valueStyle += generateFontStyleCSS(valueCustomStyle);
    }
    value = importDefaultModule(DOMPurify).sanitize(value);
    return assign(TOOLTIP_TEMPLATE, labelClass, labelStyle, label, valueClass, colorStyle, value, valueStyle);
}
//# sourceMappingURL=tooltip-utils.js.map